
import isBoolean from 'lodash/isBoolean';

import { Map, View } from 'ol';
import { defaults as defaultInteractions, DragZoom } from 'ol/interaction';
import { platformModifierKeyOnly } from 'ol/events/condition';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import {Fill, Stroke, Style} from 'ol/style.js';
import WKT from 'ol/format/WKT';
import Feature from 'ol/Feature';

import 'ol/ol.css';

import createInteractionSelectObjectPoint from './interactionMapToolSelectObjectPoint';
import createInteractionSelectObjectPolygon from './interactionMapToolSelectObjectPolygon';
import { createLayer, createLayers, createProjection } from './olUtils';
import { createCircle } from './olStyles';

export default class OpenLayersMap {

    constructor(options) {
        this._options = options || {};
        this._highlightLayer = null;
        this._highlightFeatures = [];
        this._map = null;
        this._view = null;
    }

    createFeatureFromWkt(wkt, properties) {
        let feature = new Feature({
            geometry: this.createGeometryFromWkt(wkt)
        });
        feature.setProperties(properties);
        return feature;
    }

    createGeometryFromWkt(wkt) {
        let format = new WKT();
        return format.readGeometry(wkt);
    }

    createHighlightLayer() {

        let style = new Style({
            image: createCircle(
                10, 'rgba(0, 0, 255, 0.5)', 3, 'rgba(0, 0, 255, 0.5)', {}
            ),
            stroke: new Stroke({
                color: 'rgba(255, 0, 0, 0.5)',
                width: 5
            }),
            fill: new Fill({
                color: 'rgba(255, 0, 0, 0.2)'
            })
        });

        this._highlightLayer = new VectorLayer({
            source: new VectorSource(),
            style: style
        });

        return this._highlightLayer;
    }

    createLayer(layerDefinition) {
        return createLayer(layerDefinition);
    }

    createView(mapProjectDefinition, viewState) {
        let options = {
            ...viewState,
            projection: createProjection(mapProjectDefinition.projection),
            resolutions: mapProjectDefinition.resolutions,
            minZoom: mapProjectDefinition.minZoom,
            maxZoom: mapProjectDefinition.maxZoom
        };

        this._view = new View(options);

        this._view.on('propertychange',
            (e) => {
                switch (e.key) {
                    case 'center':
                        this.changeView(e.target.get(e.key), null, null, null);
                        break;
                    case 'resolution':
                        this.changeView(
                            null,
                            this._view.getZoomForResolution(e.target.get(e.key)),
                            null,
                            e.target.get(e.key)
                        );
                        break;
                    case 'rotation':
                        this.changeView(null, null, e.target.get(e.key), null);
                        break;
                    default:
                        break;
                }
            }
        );
    }

    createMap(mapContainer, mapProjectDefinition, viewState) {
        this.createView(mapProjectDefinition, viewState);

        this._map = new Map({
            target: mapContainer,
            controls: [],
            interactions: this.getInteractions(),
            layers: [
                ...createLayers(mapProjectDefinition.layers),
                this.createHighlightLayer()
            ],
            view: this._view
        });

        this._map.on('pointermove', this.handlePointerMove);
        this._map.on('click', this.handleClick);

        this.changeLayerVisibility(viewState.layers);

        if (viewState.center && viewState.zoom) {
            //
        } else {
            if (viewState.defaultExtent) {
                this.fit(viewState.defaultExtent);
            }
        }
    }

    changeLayerVisibility(virtualLayers) {
        let layers = {};

        virtualLayers.forEach(virtualLayer => {
            virtualLayer.layers.forEach(item => {
                layers[item] = layers[item] || virtualLayer.visible;
            })
        });

        this._map.getLayers().forEach(layer => {
            if (isBoolean(layers[layer.get('id')])) {
                layer.setVisible(layers[layer.get('id')] || false);
            }
        });
    }

    changeView(center, zoom, rotation, resolution) {
        if (this._options.onChangeView) {
            this._options.onChangeView(center, zoom, rotation, resolution);
        }
    }

    fit(extent) {
        this._view.fit(extent);
    }

    getFeaturesAtPixel(pixel) {
        let features = [];
        this._map.forEachFeatureAtPixel(pixel, (feature) => {
            return features.push(feature);
        });
        return features;
    }

    getFeaturesFromEvent(evt) {
        let pixel = this._map.getEventPixel(evt.originalEvent);
        let features = this.getFeaturesAtPixel(pixel);
        return features;
    }

    getHighlightLayer() {
        return this._highlightLayer;
    }

    getInteractions() {
        let interactions = defaultInteractions({
            shiftDragZoom : false
        });
        interactions.push(new DragZoom({
            condition: platformModifierKeyOnly
        }));
        interactions.push(createInteractionSelectObjectPoint({
            onMapToolExecute: this._options.onMapToolExecute
        }));
        interactions.push(createInteractionSelectObjectPolygon({
            onMapToolExecute: this._options.onMapToolExecute
        }));
        return interactions;
    }

    getLayerById(layerId) {
        let layer;
        this._map.getLayers().forEach(item => {
            if (item.get('id') === layerId) {
                layer = item;
            }
        });
        return layer;
    }

    getMap() {
        return this._map;
    }

    getView() {
        return this._view;
    }

    handleClick = (evt) => {
        if (this._options.onFeatureClick) {
            let features = this.getFeaturesFromEvent(evt);
            if (features && (features.length > 0)) {
                // ToDo: may be return all features
                this._options.onFeatureClick(features[0]);
            }
        }

        if (this._options.onMapClick) {
            let coord = this._map.getEventCoordinate(evt.originalEvent);
            this._options.onMapClick(coord);
        }
    }

    handlePointerMove = (evt) => {
        if (evt.dragging) {
            return;
        }

        let features = this.getFeaturesFromEvent(evt);

        this._highlightFeatures.forEach(item => {
            item.setStyle(null);
        });
        this._highlightFeatures = [];

        features.forEach(feature => {
            let layer = this.getLayerById(feature.layerId);
            if (layer) {
                let highlightStyle = layer.get('highlightStyle');
                if (highlightStyle) {
                    feature.setStyle(highlightStyle);
                    this._highlightFeatures.push(feature);
                }
            }
        });
    }

    setCenter(center) {
        this._view.setCenter(center);
    }

    setZoom(zoom) {
        this._view.setZoom(zoom);
    }
}